/**
 * UnifiedFilterBar: shared filter bar for debug and journal tabs.
 *
 * - Always visible: search input + date range inputs
 * - Toggle panel: additional filters depending on mode (debug vs journal)
 */
import { useState } from "react";
import { SearchLg, FilterLines, XClose } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import type { SelectItemType } from "@/components/base/select/select";
import { Checkbox } from "@/components/base/checkbox/checkbox";

// ============================================================================
// Types
// ============================================================================

export interface UnifiedFilterBarProps {
    mode: "debug" | "journal";

    // Common filters
    search: string;
    onSearchChange: (value: string) => void;
    dateFrom: string | Date | null;
    dateTo: string | Date | null;
    onDateFromChange: (value: string) => void;
    onDateToChange: (value: string) => void;

    // Debug mode filters
    companyId?: string;
    companies?: Array<{ id: string; label: string }>;
    onCompanyChange?: (id: string) => void;
    userId?: string;
    users?: Array<{ id: string; label: string }>;
    onUserChange?: (id: string) => void;
    emailAccountId?: string;
    emailAccounts?: Array<{ id: string; label: string }>;
    onEmailAccountChange?: (id: string) => void;
    includeLimbo?: boolean;
    onIncludeLimboChange?: (value: boolean) => void;
    isDisabled?: boolean;

    // Journal mode filters
    status?: string | null;
    onStatusChange?: (value: string | null) => void;
    triggerType?: string | null;
    onTriggerTypeChange?: (value: string | null) => void;

    /** Hide the search input (when search is provided externally by parent) */
    hideSearch?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const TRIGGER_TYPE_OPTIONS: SelectItemType[] = [
    { id: "__all__", label: "All triggers" },
    { id: "email_sync", label: "Email sync" },
    { id: "document_extraction", label: "Document extraction" },
    { id: "cross_check", label: "Cross-check" },
    { id: "debug_reprocess", label: "Debug reprocess" },
    { id: "tracking_sync", label: "Tracking sync" },
    { id: "risk_assessment", label: "Risk assessment" },
];

const STATUS_OPTIONS: SelectItemType[] = [
    { id: "__all__", label: "All statuses" },
    { id: "completed", label: "Completed" },
    { id: "failed", label: "Failed" },
    { id: "running", label: "Running" },
    { id: "partial", label: "Partial" },
];

// ============================================================================
// Helpers
// ============================================================================

function countActiveFilters(props: UnifiedFilterBarProps): number {
    let count = 0;
    if (props.mode === "debug") {
        if (props.companyId) count++;
        if (props.userId) count++;
        if (props.emailAccountId) count++;
        if (props.includeLimbo) count++;
    } else {
        if (props.status) count++;
        if (props.triggerType) count++;
        if (props.dateFrom || props.dateTo) count++;
    }
    return count;
}

// ============================================================================
// Component
// ============================================================================

export function UnifiedFilterBar(props: UnifiedFilterBarProps) {
    const {
        mode,
        search,
        onSearchChange,
        dateFrom,
        dateTo,
        onDateFromChange,
        onDateToChange,
    } = props;

    const [showFilters, setShowFilters] = useState(false);
    const activeCount = countActiveFilters(props);

    const placeholder =
        mode === "debug"
            ? "Search by subject or full-text..."
            : "Search container, trigger, error...";



    return (
        <div className="space-y-2">
            {/* Search + date range + filter toggle row */}
            <div className="flex items-end gap-2">
                {!props.hideSearch && (
                    <div className="flex-1">
                        <Input
                            placeholder={placeholder}
                            value={search}
                            onChange={onSearchChange}
                            icon={SearchLg}
                            size="sm"
                        />
                    </div>
                )}

                {/* Date range: same From/To inputs for both modes */}
                <div className="w-36">
                    <Input
                        label="From"
                        type="date"
                        value={typeof dateFrom === "string" ? dateFrom : ""}
                        onChange={onDateFromChange}
                        isDisabled={props.isDisabled}
                        size="sm"
                    />
                </div>
                <div className="w-36">
                    <Input
                        label="To"
                        type="date"
                        value={typeof dateTo === "string" ? dateTo : ""}
                        onChange={onDateToChange}
                        isDisabled={props.isDisabled}
                        size="sm"
                    />
                </div>

                {/* Filter toggle button */}
                <button
                    type="button"
                    onClick={() => setShowFilters((v) => !v)}
                    className={[
                        "relative flex items-center justify-center rounded-lg p-2 ring-1 ring-inset transition-colors shrink-0",
                        showFilters || activeCount > 0
                            ? "ring-brand-300 bg-brand-primary_alt text-brand-600"
                            : "ring-primary bg-primary text-tertiary hover:bg-secondary",
                    ].join(" ")}
                    title="Toggle filters"
                >
                    <FilterLines className="h-4 w-4" />
                    {activeCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 text-[10px] font-bold text-white">
                            {activeCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Collapsible filter panel */}
            {showFilters && (
                <div className="space-y-3 rounded-lg border border-secondary bg-secondary p-3">
                    {mode === "debug" && (
                        <>
                            {/* Company selector (searchable) */}
                            <div>
                                <Select.ComboBox
                                    label="Company *"
                                    selectedKey={props.companyId || null}
                                    onSelectionChange={(key) =>
                                        props.onCompanyChange?.(String(key ?? ""))
                                    }
                                    placeholder="Search company..."
                                    items={props.companies ?? []}
                                    isDisabled={props.isDisabled}
                                >
                                    {(item) => (
                                        <Select.Item
                                            key={item.id}
                                            id={item.id}
                                            textValue={item.label}
                                        >
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select.ComboBox>
                            </div>

                            {/* User selector (searchable) */}
                            <div>
                                <Select.ComboBox
                                    key={`user-${props.companyId}`}
                                    label="User"
                                    selectedKey={props.userId || null}
                                    onSelectionChange={(key) =>
                                        props.onUserChange?.(String(key ?? ""))
                                    }
                                    placeholder="Search user..."
                                    items={props.users ?? []}
                                    isDisabled={!props.companyId || props.isDisabled}
                                >
                                    {(item) => (
                                        <Select.Item
                                            key={item.id}
                                            id={item.id}
                                            textValue={item.label}
                                        >
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select.ComboBox>
                            </div>

                            {/* Email account selector */}
                            <div>
                                <label className="block text-xs font-medium text-tertiary mb-1">
                                    Email Account
                                </label>
                                <Select
                                    key={`account-${props.companyId}-${props.userId}`}
                                    selectedKey={props.emailAccountId || null}
                                    onSelectionChange={(key) =>
                                        props.onEmailAccountChange?.(String(key ?? ""))
                                    }
                                    placeholder="All accounts"
                                    items={props.emailAccounts ?? []}
                                    isDisabled={!props.companyId || props.isDisabled}
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>
                            </div>

                            {/* Include limbo */}
                            <Checkbox
                                isSelected={props.includeLimbo ?? false}
                                onChange={(val) => props.onIncludeLimboChange?.(val)}
                                isDisabled={props.isDisabled}
                                label="Include limbo emails"
                            />
                        </>
                    )}

                    {mode === "journal" && (
                        <>
                            {/* Status */}
                            <div>
                                <label className="block text-xs font-medium text-tertiary mb-1">
                                    Status
                                </label>
                                <Select
                                    placeholder="All statuses"
                                    items={STATUS_OPTIONS}
                                    selectedKey={props.status ?? "__all__"}
                                    onSelectionChange={(key) => {
                                        props.onStatusChange?.(
                                            key === "__all__" ? null : (key as string)
                                        );
                                    }}
                                    size="sm"
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>
                            </div>

                            {/* Trigger type */}
                            <div>
                                <label className="block text-xs font-medium text-tertiary mb-1">
                                    Trigger type
                                </label>
                                <Select
                                    placeholder="All triggers"
                                    items={TRIGGER_TYPE_OPTIONS}
                                    selectedKey={props.triggerType ?? "__all__"}
                                    onSelectionChange={(key) => {
                                        props.onTriggerTypeChange?.(
                                            key === "__all__" ? null : (key as string)
                                        );
                                    }}
                                    size="sm"
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>
                            </div>
                        </>
                    )}

                    {/* Clear all filters */}
                    {activeCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowFilters(false)}
                            className="flex items-center gap-1 text-xs text-error-primary hover:text-error-700 font-medium"
                        >
                            <XClose className="h-3 w-3" />
                            Close filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
